/**
 * SkeletonLoader - Animated loading placeholders
 *
 * Provides shimmer-animated skeletons for various content types
 * to improve perceived performance during data loading.
 *
 * Features:
 * - Multiple variants (text, title, circle, card, metric)
 * - Shimmer animation
 * - Customizable width and height
 * - Respects prefers-reduced-motion
 */

interface SkeletonLoaderProps {
  variant?: 'text' | 'title' | 'circle' | 'card' | 'metric' | 'custom';
  width?: string | number;
  height?: string | number;
  count?: number;
  style?: React.CSSProperties;
}

export function SkeletonLoader({
  variant = 'text',
  width,
  height,
  count = 1,
  style = {}
}: SkeletonLoaderProps) {
  const getSkeletonStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      width: width || '100%',
      height: height || 'auto',
      ...style
    };

    switch (variant) {
      case 'text':
        return {
          ...baseStyle,
          height: height || '16px',
          width: width || '100%',
          marginBottom: '8px'
        };

      case 'title':
        return {
          ...baseStyle,
          height: height || '24px',
          width: width || '60%',
          marginBottom: '12px'
        };

      case 'circle':
        return {
          ...baseStyle,
          width: width || '40px',
          height: height || '40px',
          borderRadius: '50%'
        };

      case 'card':
        return {
          ...baseStyle,
          height: height || '120px',
          borderRadius: '12px',
          padding: '16px'
        };

      case 'metric':
        return {
          ...baseStyle,
          height: height || '80px',
          borderRadius: '8px',
          padding: '12px'
        };

      case 'custom':
      default:
        return baseStyle;
    }
  };

  const skeletonStyle = getSkeletonStyle();

  if (count > 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Array.from({ length: count }).map((_, idx) => (
          <div key={idx} className="skeleton" style={skeletonStyle} />
        ))}
      </div>
    );
  }

  return <div className="skeleton" style={skeletonStyle} />;
}

/**
 * MetricCardSkeleton - Skeleton for metric/KPI cards
 */
export function MetricCardSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '20px'
    }}>
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <SkeletonLoader variant="text" width="60%" height="14px" />
            <SkeletonLoader variant="title" width="80%" height="28px" />
            <SkeletonLoader variant="text" width="40%" height="12px" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * TableSkeleton - Skeleton for data tables
 */
export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: '16px',
        padding: '16px',
        borderBottom: '1px solid var(--border)'
      }}>
        {Array.from({ length: columns }).map((_, idx) => (
          <SkeletonLoader key={idx} variant="text" width="70%" height="14px" />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: '16px',
            padding: '16px',
            borderBottom: rowIdx < rows - 1 ? '1px solid var(--border)' : 'none'
          }}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <SkeletonLoader
              key={colIdx}
              variant="text"
              width={colIdx === 0 ? '60%' : '80%'}
              height="14px"
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * ChartSkeleton - Skeleton for chart containers
 */
export function ChartSkeleton({ height = '300px' }: { height?: string }) {
  return (
    <div className="card">
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <SkeletonLoader variant="circle" width="24px" height="24px" />
        <SkeletonLoader variant="title" width="40%" height="20px" />
      </div>
      <SkeletonLoader
        variant="custom"
        height={height}
        style={{ borderRadius: '8px' }}
      />
    </div>
  );
}

/**
 * PortfolioDashboardSkeleton - Full skeleton for portfolio dashboard
 */
export function PortfolioDashboardSkeleton() {
  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Stats Banner Skeleton */}
      <div className="card" style={{ marginBottom: '20px', padding: '16px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px'
        }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <SkeletonLoader variant="text" width="60%" height="12px" style={{ margin: '0 auto 8px' }} />
              <SkeletonLoader variant="title" width="80%" height="20px" style={{ margin: '0 auto' }} />
            </div>
          ))}
        </div>
      </div>

      {/* Metric Cards Skeleton */}
      <MetricCardSkeleton count={4} />

      {/* Chart Skeleton */}
      <ChartSkeleton height="400px" />

      {/* Table Skeleton */}
      <div style={{ marginTop: '20px' }}>
        <TableSkeleton rows={6} columns={5} />
      </div>
    </div>
  );
}
